// Pure derivation helpers for the Roadmap view. Computes the
// shapes the React grid components consume from the per-view
// payload (`useRoadmapView` → /v1/orgs/:slug/views/roadmap).
//
// Stays React-agnostic — every function takes data + state and
// returns data. Component code lives under `views/roadmap/`.

import type { BusinessFunction } from '@lib/api-types/business-functions';
import type {
  DerivedContext,
  Skill,
  SkillKind,
} from '@lib/api-types/derived-contexts';
import type { Engineer } from '@lib/api-types/engineers';
import type { Project } from '@lib/api-types/projects';
import type { Rating, RatingValue } from '@lib/api-types/ratings';
import type { Roadmap } from '@lib/api-types/roadmaps';
import type { TeamChange } from '@lib/api-types/team-changes';

import {
  buildRatingIndex,
  buildSkillFunctions,
  indexSkills,
  isAgent,
  isDefaultVisibleSkill,
  isInstitutional,
  type RatingIndex,
} from './data-views.ts';
import {
  buildSkillToComps,
  normalizeLegacyReqMap,
  reqKey,
} from './views/roadmap/req-key.ts';

// ===== Types ===========================================================

export type RoadmapViewMode = 'resource' | 'impact' | 'diff';

/**
 * Session-scoped Roadmap-view state. Lives in a `useReducer` in
 * `RoadmapView.tsx`; selectors below operate on this shape.
 *
 * `selectedProjectIds` + `selectedTeamChangeId` are the single source
 * of truth for what's currently rendered, regardless of whether a
 * roadmap is active. When the user picks a roadmap, the reducer
 * loads `rm.projects` + `rm.team_change` into these fields; the user
 * can then toggle items on/off and the deltas drive the "Update
 * Roadmap" dirty check. With no roadmap active, the selection is
 * free-form and the "Create Roadmap" CTA captures it.
 */
export interface RoadmapState {
  roadmapId: string | null;
  /**
   * Currently rendered projects (multi-select). When a roadmap is
   * active, initialized from `rm.projects` on `select-roadmap`; the
   * user can then add or remove plans. Order doesn't carry meaning
   * — comparisons treat it as a set.
   */
  selectedProjectIds: string[];
  /**
   * Currently rendered team-change (single). When a roadmap is
   * active, initialized from `rm.team_change` on `select-roadmap`;
   * null means no team-change is layered. The user can pick a
   * different plan to swap, or click the selected one again to
   * clear.
   */
  selectedTeamChangeId: string | null;
  view: RoadmapViewMode;
  forecastOpen: boolean;
}

export interface MergedSkill extends Skill {
  is_overlay: boolean;
}

/**
 * Extends DerivedContext with overlay tagging. `skills` is narrowed to
 * `MergedSkill[]`. The wire `DerivedContext` already carries `skills` +
 * `functions` + `subtitle` + timestamps, so MergedDerivedContext just
 * adds the overlay flags.
 */
export interface MergedDerivedContext extends Omit<DerivedContext, 'skills'> {
  skills: MergedSkill[];
  is_overlay: boolean;
}

export interface MergedFunction extends BusinessFunction {
  is_overlay: boolean;
}

export interface ProjectDemand {
  skill: string;
  role: string;
  level: RatingValue;
}

export interface ProjectContribution extends ProjectDemand {
  /** Display name of the contributing project (aggregate-mode only). */
  project: string;
}

export interface ProjectImpactCell {
  maxLevel: number;
  /** Distinct profiles (project, role) demanding this cell. Headcount-shaped, matches Resource columns. */
  count: number;
  /** Total skill-demands in this cell — N skills × 1 role still counts as N. */
  skillCount: number;
  demands: ProjectDemand[];
  /** Populated only on aggregate ProjectViews; one entry per contributing project demand. */
  contributions?: ProjectContribution[];
}

export interface ProjectRole {
  id: string;
  display: string;
}

export interface ProjectView {
  id: string;
  display: string;
  status: string;
  prompt: string | null;
  mergedDerivedContexts: MergedDerivedContext[];
  mergedFunctions: MergedFunction[];
  impactGrid: Record<string, Record<string, ProjectImpactCell>>;
  roles: ProjectRole[];
  requirementsByRole: Record<string, Record<string, { level: RatingValue }>>;
  /**
   * Per `reqKey(comp, skill)` → functions the project assigned BEYOND the
   * skill's catalog footprint (user-added "net-new" associations). The flat
   * Impact/Diff (skill) view renders these cells with the newly-added
   * treatment; the grouped (derivedContext) view lights them but without it.
   */
  newSkillFunctions?: Record<string, string[]>;
  isAggregate?: boolean;
  /** Aggregate-only: max level required across any project × role per skill. */
  maxRequirements?: Record<string, RatingValue>;
  /** Aggregate-only: per-project requirement breakdown for tooltip attribution. */
  projectGroups?: Array<{
    id: string;
    display: string;
    roles: ProjectRole[];
    requirementsByRole: Record<string, Record<string, { level: RatingValue }>>;
  }>;
}

export interface UpskillDelta {
  from: RatingValue | null;
  to: RatingValue;
}

/**
 * Engineer-shaped column in the projected roster. Built from the
 * wire `Engineer` (live members) or `TeamChange.hires[]` entries
 * (incoming hires). `display_name` mirrors the wire field name so
 * sub-components don't translate.
 */
export interface ProjectedTalentColumn {
  id: string;
  display_name: string;
  github_login: string | null;
  kind: 'human' | 'agent';
  is_new: boolean;
}

export interface ProjectedDeparture {
  id: string;
  display_name: string;
  github_login: string | null;
}

export interface ProjectedRoster {
  id: string;
  display: string;
  talentColumns: ProjectedTalentColumn[];
  departedHumans: ProjectedDeparture[];
  ratings: RatingIndex;
  upskillDeltas: Record<string, Record<string, UpskillDelta>>;
}

/**
 * Shared shape passed to executability/supply helpers and the
 * Resource + Diff views. `teamColumns` is `Engineer[]` for the live
 * context and `ProjectedTalentColumn[]` when a team-change
 * projection is active — both expose `id`, `display_name`, and
 * `kind`, so callers stay polymorphic via `'kind' in eng ? eng.kind : 'human'`.
 */
export interface RoadmapProjectionContext {
  teamColumns: Array<Engineer | ProjectedTalentColumn>;
  ratings: RatingIndex;
  departedHumans: ProjectedDeparture[];
  upskillDeltas: Record<string, Record<string, UpskillDelta>>;
  fromProjection: boolean;
  teamChangeId: string | null;
  teamChangeDisplay: string | null;
}

export type ExecutabilityClass = 'gap' | 'fragile' | 'covered';

export interface ExecutabilityRow {
  skillId: string;
  skillDisplay: string;
  level: RatingValue;
  count: number;
  cls: ExecutabilityClass;
  qualified: string[];
}

const DEFAULT_ROLE: ProjectRole = { id: 'primary', display: 'Build' };

/**
 * Roadmap input bundle — the per-entity slices a roadmap-flavored
 * read returns. Mirrors the `RoadmapView` payload but used as a
 * structural parameter so test fixtures and the live SPA pass the
 * same shape.
 */
export interface RoadmapInput {
  derivedContexts: DerivedContext[];
  business_functions: BusinessFunction[];
  engineers: Engineer[];
  ratings: Rating[];
  projects: Project[];
  roadmaps: Roadmap[];
  team_changes: TeamChange[];
}

// ===== Overlay merge ===================================================

interface MergeResult {
  mergedDerivedContexts: DerivedContext[];
  mergedFunctions: BusinessFunction[];
  overlayCompIds: Set<string>;
  overlaySkillIds: Set<string>;
  overlayFnIds: Set<string>;
}

function cloneDerivedContext(c: DerivedContext): DerivedContext {
  return {
    ...c,
    skills: c.skills.map((s) => ({ ...s })),
    functions: [...c.functions],
  };
}

function functionFromOverlay(f: { id: string; display: string; subtitle?: string }, ts: number): BusinessFunction {
  // Overlay-introduced functions don't have persisted timestamps;
  // synthesize from `ts` so the wire type's required fields are
  // satisfied. Downstream renderers don't compare _at fields.
  return {
    object: 'business_function' as const,
    id: f.id,
    display: f.display,
    ...(f.subtitle != null ? { subtitle: f.subtitle } : {}),
    created_at: ts,
    updated_at: ts,
  };
}

function overlaySkillToSkill(s: { id: string; display: string; kind: SkillKind }): Skill {
  return { id: s.id, display: s.display, kind: s.kind };
}

/**
 * Apply a project's overlay onto the live derivedContext / function
 * lists. Returns merged lists plus id-sets marking which entries
 * originated in the overlay so views can render them with "NEW"
 * decoration.
 */
export function mergeOverlay(
  overlay: Project['overlay'] | undefined,
  baseDerivedContexts: DerivedContext[],
  baseFunctions: BusinessFunction[],
): MergeResult {
  const ovl = overlay;
  const compMap = new Map<string, DerivedContext>(
    baseDerivedContexts.map((c) => [c.id, cloneDerivedContext(c)]),
  );
  const overlayCompIds = new Set<string>();
  const overlaySkillIds = new Set<string>();

  if (ovl) {
    for (const entry of ovl.new_skills_in_existing) {
      const comp = compMap.get(entry.derivedContext);
      if (!comp) continue;
      if (!comp.skills.some((s) => s.id === entry.skill.id)) {
        comp.skills.push(overlaySkillToSkill(entry.skill));
        overlaySkillIds.add(entry.skill.id);
      }
    }

    for (const nc of ovl.new_derived_contexts) {
      overlayCompIds.add(nc.id);
      compMap.set(nc.id, {
        object: 'derived_context' as const,
        id: nc.id,
        display: nc.display,
        subtitle: null,
        skills: nc.skills.map(overlaySkillToSkill),
        functions: [...nc.functions],
        // Overlay-introduced derivedContexts aren't persisted; satisfy
        // the wire shape with synthesized timestamps.
        created_at: 0,
        updated_at: 0,
      });
      for (const s of nc.skills) overlaySkillIds.add(s.id);
    }
  }

  const ts = Math.floor(Date.now() / 1000);
  const mergedFunctions: BusinessFunction[] = [
    ...baseFunctions.map((f) => ({ ...f })),
    ...(ovl ? ovl.new_functions.map((f) => functionFromOverlay(f, ts)) : []),
  ];
  const overlayFnIds = new Set<string>(ovl ? ovl.new_functions.map((f) => f.id) : []);

  if (ovl) {
    for (const [fnId, compIds] of Object.entries(ovl.new_function_derived_contexts)) {
      for (const cid of compIds) {
        const comp = compMap.get(cid);
        if (comp && !comp.functions.includes(fnId)) comp.functions.push(fnId);
      }
    }
  }

  return {
    mergedDerivedContexts: [...compMap.values()],
    mergedFunctions,
    overlayCompIds,
    overlaySkillIds,
    overlayFnIds,
  };
}

// ===== buildProjectViews ===============================================

function decorateProjectView(
  proj: Project,
  baseDerivedContexts: DerivedContext[],
  baseFunctions: BusinessFunction[],
): ProjectView {
  const {
    mergedDerivedContexts,
    mergedFunctions,
    overlayCompIds,
    overlaySkillIds,
    overlayFnIds,
  } = mergeOverlay(proj.overlay, baseDerivedContexts, baseFunctions);

  const roles: ProjectRole[] =
    proj.requirements.roles.length > 0
      ? proj.requirements.roles
      : [DEFAULT_ROLE];

  const requirementsByRole: Record<
    string,
    Record<string, { level: RatingValue }>
  > = {};
  for (const role of roles) requirementsByRole[role.id] = {};
  for (const r of proj.requirements.skills) {
    const roleId = r.role || DEFAULT_ROLE.id;
    (requirementsByRole[roleId] ??= {})[r.skill] = { level: r.level };
  }

  // Each (comp, fn) cell collects every (skill, role, level) demand
  // that lands there — a skill demands a cell when the skill is in
  // the comp's skill list AND the comp serves the function. Color =
  // MAX level, count = distinct profiles (roles) demanding the cell
  // so the number reads as headcount and lines up with Resource-view
  // columns; skillCount preserves the raw skill-demand total for
  // tooltip detail.
  const impactGrid: Record<string, Record<string, ProjectImpactCell>> = {};
  for (const comp of mergedDerivedContexts) {
    const compCells: Record<string, ProjectImpactCell> = {};
    impactGrid[comp.id] = compCells;
    const compSkillIds = new Set(comp.skills.map((s) => s.id));
    const compServesFn = new Set(comp.functions);
    for (const fn of mergedFunctions) {
      const demands: ProjectDemand[] = [];
      if (compServesFn.has(fn.id)) {
        for (const role of roles) {
          const roleReqs = requirementsByRole[role.id] ?? {};
          for (const [skillId, req] of Object.entries(roleReqs)) {
            if (!compSkillIds.has(skillId)) continue;
            demands.push({
              skill: skillId,
              role: role.id,
              level: req.level,
            });
          }
        }
      }
      const maxLevel = demands.reduce((m, d) => Math.max(m, d.level), 0);
      const profiles = new Set(demands.map((d) => d.role));
      compCells[fn.id] = {
        maxLevel,
        count: profiles.size,
        skillCount: demands.length,
        demands,
      };
    }
  }

  return {
    id: proj.id,
    display: proj.display,
    status: proj.status,
    prompt: proj.prompt ?? null,
    mergedDerivedContexts: mergedDerivedContexts.map((c) => ({
      ...c,
      skills: c.skills.map((s) => ({
        ...s,
        is_overlay: overlaySkillIds.has(s.id),
      })),
      is_overlay: overlayCompIds.has(c.id),
    })),
    mergedFunctions: mergedFunctions.map((f) => ({
      ...f,
      is_overlay: overlayFnIds.has(f.id),
    })),
    impactGrid,
    roles,
    requirementsByRole,
  };
}

export function buildProjectViews(input: {
  projects: Project[];
  derivedContexts: DerivedContext[];
  business_functions: BusinessFunction[];
}): Map<string, ProjectView> {
  const result = new Map<string, ProjectView>();
  for (const proj of input.projects) {
    result.set(
      proj.id,
      decorateProjectView(proj, input.derivedContexts, input.business_functions),
    );
  }
  return result;
}

// ===== planToProjectView ===============================================
//
// Saved project-kind plans (the `plans` D1 table) carry the scaffold's
// authored state: roles + per-(role, skill) levels + optional
// per-(role, skill) function assignments + deprecation flags +
// freshly-added derivedContexts / skills. They render through the same
// `ProjectView` shape as legacy Project entities, so the Impact and
// Diff grids stay schema-agnostic.
//
// Notable mappings vs `decorateProjectView`:
//   - Deprecated cells (payload.deprecated) are skipped from
//     requirementsByRole + impactGrid entirely — the scaffold treats
//     them as "this role no longer needs this skill", so they
//     shouldn't contribute demand.
//   - The `(comp, fn)` cell credits demand from `(role, skill)` when
//     comp.skills contains the skill AND fn is in the user's explicit
//     `function_assignments[role][skill]` if set, otherwise
//     `comp.functions` (matches the scaffold's "absent = use defaults"
//     semantic from the function picker).
//   - Plan-added derivedContexts have no implicit `functions`, so unless
//     the user has filled in `function_assignments`, demand spreads
//     across every function so the Diff view still has cells to render
//     supply-minus-demand math against. The Impact heatmap reads the
//     same impactGrid, so overlay comps light up uniformly there too.

type ProjectPlanPayload = {
  roles: ProjectRole[];
  added_derived_contexts: Array<{ id: string; display: string }>;
  added_skills: Record<string, Array<{ id: string; display: string }>>;
  comp_overrides: Record<string, string>;
  requirements: Record<string, Record<string, RatingValue>>;
  deprecated: Record<string, Record<string, true>>;
  function_assignments: Record<string, Record<string, string[]>>;
};

type ProjectPlan = {
  id: string;
  display: string;
  kind: 'project';
  payload: ProjectPlanPayload;
};

function planToProjectView(
  plan: ProjectPlan,
  baseDerivedContexts: DerivedContext[],
  baseFunctions: BusinessFunction[],
): ProjectView {
  const { payload } = plan;

  // ---- mergedDerivedContexts (base + added overlay) ----
  const compMap = new Map<string, MergedDerivedContext>();
  for (const c of baseDerivedContexts) {
    compMap.set(c.id, {
      ...c,
      skills: c.skills.map((s) => ({ ...s, is_overlay: false })),
      is_overlay: false,
    });
  }
  for (const ac of payload.added_derived_contexts) {
    compMap.set(ac.id, {
      object: 'derived_context' as const,
      id: ac.id,
      display: ac.display,
      subtitle: null,
      functions: [],
      created_at: 0,
      updated_at: 0,
      skills: [],
      is_overlay: true,
    });
  }
  // Append payload.added_skills onto whatever comp owns them (base or new).
  for (const [compId, extras] of Object.entries(payload.added_skills)) {
    const comp = compMap.get(compId);
    if (!comp) continue;
    for (const s of extras) {
      if (comp.skills.some((existing) => existing.id === s.id)) continue;
      comp.skills.push({
        id: s.id,
        display: s.display,
        // Scaffold doesn't capture skill kind; default to framework so
        // it surfaces in default catalog views (libraries are hidden
        // by default — see `isDefaultVisibleSkill`).
        kind: 'framework' as const,
        is_overlay: true,
      });
    }
  }
  // Inline comp-display overrides from the scaffold's rename affordance.
  for (const [compId, display] of Object.entries(payload.comp_overrides)) {
    const comp = compMap.get(compId);
    if (comp) comp.display = display;
  }

  // ---- mergedFunctions (plans don't add new functions) ----
  const mergedFunctions: MergedFunction[] = baseFunctions.map((f) => ({
    ...f,
    is_overlay: false,
  }));

  // ---- roles ----
  const roles: ProjectRole[] =
    payload.roles.length > 0 ? payload.roles : [DEFAULT_ROLE];

  // Finalize derivedContexts first — the demand-side maps are scoped by
  // (derivedContext, skill), so we need the merged set to upgrade legacy
  // (skill-only) payloads and to look requirements up per derivedContext.
  const mergedDerivedContexts = [...compMap.values()];
  const skillToComps = buildSkillToComps(mergedDerivedContexts);
  const requirements = normalizeLegacyReqMap(payload.requirements, skillToComps);
  const deprecated = normalizeLegacyReqMap(payload.deprecated, skillToComps);
  const functionAssignments = normalizeLegacyReqMap(
    payload.function_assignments,
    skillToComps,
  );

  // ---- requirementsByRole (composite comp::skill keys, skip deprecated) ----
  const requirementsByRole: Record<
    string,
    Record<string, { level: RatingValue }>
  > = {};
  for (const role of roles) requirementsByRole[role.id] = {};
  for (const role of roles) {
    const reqs = requirements[role.id] ?? {};
    const depr = deprecated[role.id] ?? {};
    for (const [key, level] of Object.entries(reqs)) {
      if (depr[key]) continue;
      requirementsByRole[role.id]![key] = { level };
    }
  }

  // ---- impactGrid ----
  // Default creditable functions for a skill = its catalog "footprint": the
  // union of functions across every derivedContext that lists the skill (the
  // same source the Resilience platform map renders). This is WIDER than the
  // derivedContext's own `functions_json`, so a project lights the same
  // intersections users see on the platform map instead of under-lighting.
  const catalogSkillFns = buildSkillFunctions(mergedDerivedContexts);
  const impactGrid: Record<string, Record<string, ProjectImpactCell>> = {};
  for (const comp of mergedDerivedContexts) {
    const compCells: Record<string, ProjectImpactCell> = {};
    impactGrid[comp.id] = compCells;
    for (const fn of mergedFunctions) {
      const demands: ProjectDemand[] = [];
      for (const role of roles) {
        const roleReqs = requirementsByRole[role.id] ?? {};
        const roleAssignments = functionAssignments[role.id] ?? {};
        for (const skill of comp.skills) {
          const key = reqKey(comp.id, skill.id);
          const req = roleReqs[key];
          if (!req) continue;
          const explicit = roleAssignments[key];
          const skillFns = catalogSkillFns.get(skill.id);
          let creditable: string[];
          if (explicit !== undefined) {
            creditable = explicit;
          } else if (skillFns && skillFns.size > 0) {
            creditable = [...skillFns];
          } else if (comp.is_overlay) {
            // Overlay comp with no implicit business-function set and
            // no per-(role, skill) override from the user. Spread the
            // demand across every function so Diff's supply-minus-
            // demand math has a denominator for new comps. Without
            // this, the new-comp cells fall into the "unused capacity"
            // path on every function even when a role explicitly
            // demands the skill — which reads as "this didn't change
            // anything," masking the new project's pressure. The team-
            // change overlay (hire ratings on the new skills) flows
            // into `ctx.ratings` already, so the supply side just
            // works.
            creditable = mergedFunctions.map((f) => f.id);
          } else {
            creditable = [];
          }
          if (!creditable.includes(fn.id)) continue;
          demands.push({ skill: skill.id, role: role.id, level: req.level });
        }
      }
      const maxLevel = demands.reduce((m, d) => Math.max(m, d.level), 0);
      const profiles = new Set(demands.map((d) => d.role));
      compCells[fn.id] = {
        maxLevel,
        count: profiles.size,
        skillCount: demands.length,
        demands,
      };
    }
  }

  // ---- newSkillFunctions (assigned beyond the catalog footprint) ----
  // Per (comp, skill): the explicitly-assigned functions that are NOT in the
  // skill's catalog footprint — the user "added a skill to a business
  // function for the first time". The flat Impact/Diff view styles these
  // cells as newly-added.
  const newSkillFunctions: Record<string, string[]> = {};
  for (const comp of mergedDerivedContexts) {
    for (const skill of comp.skills) {
      const key = reqKey(comp.id, skill.id);
      const catalog = catalogSkillFns.get(skill.id) ?? new Set<string>();
      const assigned = new Set<string>();
      for (const role of roles) {
        for (const fnId of functionAssignments[role.id]?.[key] ?? []) {
          assigned.add(fnId);
        }
      }
      const net = [...assigned].filter((fnId) => !catalog.has(fnId));
      if (net.length > 0) newSkillFunctions[key] = net;
    }
  }

  return {
    id: plan.id,
    display: plan.display,
    status: 'draft',
    prompt: null,
    mergedDerivedContexts,
    mergedFunctions,
    impactGrid,
    roles,
    requirementsByRole,
    newSkillFunctions,
  };
}

/**
 * Build a `Map<planId, ProjectView>` from saved project-kind plans.
 * Same map shape as `buildProjectViews` so consumers can `Map.set` /
 * spread the two together.
 */
export function buildPlanProjectViews(input: {
  plans: Array<{ kind: 'project' | 'team_change'; id: string; display: string; payload: unknown }>;
  derivedContexts: DerivedContext[];
  business_functions: BusinessFunction[];
}): Map<string, ProjectView> {
  const result = new Map<string, ProjectView>();
  for (const plan of input.plans) {
    if (plan.kind !== 'project') continue;
    result.set(
      plan.id,
      planToProjectView(
        plan as ProjectPlan,
        input.derivedContexts,
        input.business_functions,
      ),
    );
  }
  return result;
}

// ===== buildProjectedRosters ===========================================

function asTalentColumn(e: Engineer, isNew = false): ProjectedTalentColumn {
  return {
    id: e.id,
    display_name: e.display_name,
    github_login: e.github_logins[0] ?? null,
    kind: isAgent(e) ? 'agent' : 'human',
    is_new: isNew,
  };
}

function hireToEngineer(h: TeamChange['hires'][number]): Engineer {
  // Build an Engineer-shaped object from a hire so the rating index
  // (which keys on engineer.id) sees hires as first-class members.
  // The unused-by-Roadmap fields are populated with sensible defaults.
  return {
    object: 'engineer' as const,
    id: h.id,
    display_name: h.display,
    primary_email: null,
    // Legacy TeamChangeHire.status includes 'bot' as a category mistake
    // that overlaps with `kind`. Engineer.status is just lifecycle:
    // a 'bot' hire is an active agent.
    status: h.status === 'bot' ? 'active' as const : h.status,
    scope: h.scope,
    kind: h.kind,
    git_emails: [],
    github_logins: [],
    created_at: 0,
    updated_at: 0,
  };
}

/**
 * Project the live roster forward for each team-change: drop
 * departures, append hires, overlay upskill ratings. Active agents
 * pass through unchanged. Never mutates the live data.
 */
export function buildProjectedRosters(input: {
  derivedContexts: DerivedContext[];
  engineers: Engineer[];
  ratings: Rating[];
  team_changes: TeamChange[];
}): Map<string, ProjectedRoster> {
  const result = new Map<string, ProjectedRoster>();
  const skillsById = indexSkills(input.derivedContexts);
  const activeHumanList = input.engineers.filter(
    (e) => !isAgent(e) && e.status === 'active',
  );
  const activeAgentList = input.engineers.filter(
    (e) => isAgent(e) && e.status === 'active',
  );

  for (const tc of input.team_changes) {
    const departureIds = new Set(tc.departures.map((d) => d.engineer_id));

    const projHires: Engineer[] = tc.hires.map(hireToEngineer);
    const projHireRatings: Rating[] = tc.hires.flatMap((h) =>
      h.ratings.map((r): Rating => ({
        object: 'rating' as const,
        engineer_id: h.id,
        skill_id: r.skill_id,
        value: r.rating,
        note: null,
        created_at: 0,
        updated_at: 0,
      })),
    );

    const projHumans = activeHumanList.filter((e) => !departureIds.has(e.id));
    const projDeparted = activeHumanList.filter((e) => departureIds.has(e.id));
    const projTeam: Engineer[] = [
      ...projHumans,
      ...projHires,
      ...activeAgentList,
    ];

    // Upskill ratings appended last so they last-write-win over the
    // live rating inside `buildRatingIndex`'s inner map.
    const upskillRatings: Rating[] = tc.upskills.map((u): Rating => ({
      object: 'rating' as const,
      engineer_id: u.engineer_id,
      skill_id: u.skill_id,
      value: u.to,
      note: null,
      created_at: 0,
      updated_at: 0,
    }));

    const projRatingIndex = buildRatingIndex(skillsById, projTeam, [
      ...input.ratings,
      ...upskillRatings,
      ...projHireRatings,
    ]);

    const upskillDeltas: Record<string, Record<string, UpskillDelta>> = {};
    for (const u of tc.upskills) {
      (upskillDeltas[u.engineer_id] ??= {})[u.skill_id] = {
        from: u.from,
        to: u.to,
      };
    }

    const talentColumns: ProjectedTalentColumn[] = [
      ...projHumans.map((e) => asTalentColumn(e, false)),
      ...projHires.map((e) => asTalentColumn(e, true)),
      ...activeAgentList.map((e) => asTalentColumn(e, false)),
    ];

    const departedHumans: ProjectedDeparture[] = projDeparted.map((e) => ({
      id: e.id,
      display_name: e.display_name,
      github_login: e.github_logins[0] ?? null,
    }));

    result.set(tc.id, {
      id: tc.id,
      display: tc.display,
      talentColumns,
      departedHumans,
      ratings: projRatingIndex,
      upskillDeltas,
    });
  }

  return result;
}

// ===== buildPlanProjectedRosters =======================================
//
// Saved team-change-kind plans carry the scaffold's authored state:
// added ICs, per-(engineer, skill) rating overrides (used for both
// hire ratings and upskills), departed-engineer flags, IC display
// renames. They render through the same `ProjectedRoster` shape as
// legacy `TeamChange` records, so `projectionContext` stays
// schema-agnostic.
//
// `payload.ratings` mapping: an entry whose engineer_id matches an
// added IC seeds the hire's starting rating; otherwise it's an
// upskill on an existing engineer (with `from` = live rating, `to` =
// override).
//
// Plan-added derivedContexts / skills (`payload.added_derived_contexts` +
// `payload.added_skills`) aren't projected onto the grid's row axes
// yet — that's tracked as a follow-up. Adding an IC + the IC's
// ratings on existing skills works today.

type TeamChangePlanPayload = {
  added_ics: Array<{ id: string; display: string }>;
  added_derived_contexts: Array<{ id: string; display: string }>;
  added_skills: Record<string, Array<{ id: string; display: string }>>;
  ratings: Record<string, Record<string, RatingValue>>;
  departed_ids: string[];
  ic_renames: Record<string, string>;
  function_assignments: Record<string, Record<string, string[]>>;
};

export function buildPlanProjectedRosters(input: {
  plans: Array<{ kind: 'project' | 'team_change'; id: string; display: string; payload: unknown }>;
  derivedContexts: DerivedContext[];
  engineers: Engineer[];
  ratings: Rating[];
}): Map<string, ProjectedRoster> {
  const result = new Map<string, ProjectedRoster>();
  const skillsById = indexSkills(input.derivedContexts);
  const activeHumanList = input.engineers.filter(
    (e) => !isAgent(e) && e.status === 'active',
  );
  const activeAgentList = input.engineers.filter(
    (e) => isAgent(e) && e.status === 'active',
  );
  const liveRatingIndex = buildRatingIndex(skillsById, input.engineers, input.ratings);

  for (const plan of input.plans) {
    if (plan.kind !== 'team_change') continue;
    const payload = plan.payload as TeamChangePlanPayload;

    const addedIcIds = new Set(payload.added_ics.map((ic) => ic.id));
    const departureIds = new Set(payload.departed_ids);
    const renames = payload.ic_renames;

    // Added ICs → Engineer-shaped hires (kind 'human', status active).
    const projHires: Engineer[] = payload.added_ics.map((ic) => ({
      object: 'engineer' as const,
      id: ic.id,
      display_name: renames[ic.id] ?? ic.display,
      primary_email: null,
      status: 'active' as const,
      scope: 'engineering' as const,
      kind: 'human' as const,
      git_emails: [],
      github_logins: [],
      created_at: 0,
      updated_at: 0,
    }));

    // Per-IC rating entries → hire ratings (for added) or upskills (for existing).
    const projHireRatings: Rating[] = [];
    const upskillRatings: Rating[] = [];
    const upskillDeltas: Record<string, Record<string, UpskillDelta>> = {};
    for (const [engineerId, skillRatings] of Object.entries(payload.ratings)) {
      const isHire = addedIcIds.has(engineerId);
      for (const [skillId, level] of Object.entries(skillRatings)) {
        const rating: Rating = {
          object: 'rating' as const,
          engineer_id: engineerId,
          skill_id: skillId,
          value: level,
          note: null,
          created_at: 0,
          updated_at: 0,
        };
        if (isHire) {
          projHireRatings.push(rating);
        } else {
          upskillRatings.push(rating);
          const fromRating =
            liveRatingIndex.get(engineerId)?.get(skillId) ?? null;
          (upskillDeltas[engineerId] ??= {})[skillId] = {
            from: fromRating,
            to: level,
          };
        }
      }
    }

    // Apply session display renames to live engineers + agents.
    const renamedHumans = activeHumanList.map((e) => {
      const rename = renames[e.id];
      return rename ? { ...e, display_name: rename } : e;
    });
    const renamedAgents = activeAgentList.map((e) => {
      const rename = renames[e.id];
      return rename ? { ...e, display_name: rename } : e;
    });

    const projHumans = renamedHumans.filter((e) => !departureIds.has(e.id));
    const projDeparted = renamedHumans.filter((e) => departureIds.has(e.id));
    const projTeam: Engineer[] = [
      ...projHumans,
      ...projHires,
      ...renamedAgents,
    ];

    const projRatingIndex = buildRatingIndex(skillsById, projTeam, [
      ...input.ratings,
      ...upskillRatings,
      ...projHireRatings,
    ]);

    const talentColumns: ProjectedTalentColumn[] = [
      ...projHumans.map((e) => asTalentColumn(e, false)),
      ...projHires.map((e) => asTalentColumn(e, true)),
      ...renamedAgents.map((e) => asTalentColumn(e, false)),
    ];

    const departedHumans: ProjectedDeparture[] = projDeparted.map((e) => ({
      id: e.id,
      display_name: e.display_name,
      github_login: e.github_logins[0] ?? null,
    }));

    result.set(plan.id, {
      id: plan.id,
      display: plan.display,
      talentColumns,
      departedHumans,
      ratings: projRatingIndex,
      upskillDeltas,
    });
  }

  return result;
}

// ===== applyPlanCompOverlay ============================================
//
// Layer a plan's `added_derived_contexts` / `added_skills` / `comp_overrides`
// onto a `ProjectView`'s skill-axis. Used by the Resource view to
// surface a saved team-change plan's new comps + skills as additional
// grid rows after the engineer-side projection already applied via
// `projectionContext`.
//
// Same overlay semantics as `planToProjectView` (used for project
// plans): added comps become overlay rows tagged `is_overlay: true`;
// added skills are appended to the owning comp (existing or new),
// deduped by skill id; `comp_overrides` rename inline. New derivedContexts
// have no `functions` — they show as rows in Resource (engineer ×
// skill) but won't pick up cells in Impact / Diff until the user
// explicitly assigns functions.

export interface SkillCompOverlay {
  added_derived_contexts: Array<{ id: string; display: string }>;
  added_skills: Record<string, Array<{ id: string; display: string }>>;
  comp_overrides: Record<string, string>;
}

export function applyPlanCompOverlay(
  base: ProjectView,
  overlay: SkillCompOverlay,
): ProjectView {
  const hasOverlay =
    overlay.added_derived_contexts.length > 0 ||
    Object.keys(overlay.added_skills).length > 0 ||
    Object.keys(overlay.comp_overrides).length > 0;
  if (!hasOverlay) return base;

  const wrapAddedSkill = (s: { id: string; display: string }): MergedSkill => ({
    id: s.id,
    display: s.display,
    kind: 'framework' as const,
    is_overlay: true,
  });

  // Augment existing comps: append new skills (dedup by id), apply rename.
  const augmentedExisting = base.mergedDerivedContexts.map((c) => {
    const extras = overlay.added_skills[c.id] ?? [];
    const renamed = overlay.comp_overrides[c.id];
    if (extras.length === 0 && renamed === undefined) return c;
    return {
      ...c,
      display: renamed ?? c.display,
      skills: [
        ...c.skills,
        ...extras
          .filter((s) => !c.skills.some((existing) => existing.id === s.id))
          .map(wrapAddedSkill),
      ],
    };
  });

  // Newly-added comps: skills come entirely from added_skills[comp.id].
  const existingIds = new Set(base.mergedDerivedContexts.map((c) => c.id));
  const addedMergedComps: MergedDerivedContext[] = overlay.added_derived_contexts
    .filter((c) => !existingIds.has(c.id))
    .map((c) => ({
      object: 'derived_context' as const,
      id: c.id,
      display: overlay.comp_overrides[c.id] ?? c.display,
      subtitle: null,
      functions: [],
      created_at: 0,
      updated_at: 0,
      skills: (overlay.added_skills[c.id] ?? []).map(wrapAddedSkill),
      is_overlay: true,
    }));

  return {
    ...base,
    mergedDerivedContexts: [...augmentedExisting, ...addedMergedComps],
  };
}

// ===== State selectors =================================================

/**
 * Predicate: is `projectId` in the current selection? Used by sidebar
 * checkboxes + aggregate projection helpers.
 */
export function isProjectSelected(
  state: RoadmapState,
  projectId: string,
): boolean {
  return state.selectedProjectIds.includes(projectId);
}

/**
 * Predicate: is `teamChangeId` the current team-change selection?
 */
export function isTeamChangeSelected(
  state: RoadmapState,
  teamChangeId: string,
): boolean {
  return state.selectedTeamChangeId === teamChangeId;
}

export function activeRoadmap(
  state: RoadmapState,
  roadmaps: Roadmap[],
): Roadmap | null {
  if (!state.roadmapId) return null;
  return roadmaps.find((r) => r.id === state.roadmapId) ?? null;
}

/**
 * Order-insensitive set equality on string arrays. Used to detect
 * whether the current selection has drifted from `rm.projects`.
 */
function sortedJoin(ids: string[]): string {
  return [...ids].sort().join(' ');
}

/**
 * "Dirty" check: when a roadmap is active, does the live selection
 * differ from its persisted `projects` / `team_change`? Drives the
 * Update Roadmap CTA. Returns false when no roadmap is active.
 *
 * `knownIds` (optional) lets the caller supply the universe of plan
 * ids that currently exist. Any `rm.projects` / `rm.team_change`
 * references not in that set are treated as dangling (e.g. after the
 * referenced plan was deleted) and excluded from the diff — deleting
 * a plan shouldn't, on its own, mark the roadmap dirty. Omitting the
 * param falls back to the strict comparison.
 */
export function isRoadmapDirty(
  state: RoadmapState,
  roadmaps: Roadmap[],
  knownIds?: { projects?: Set<string>; teamChanges?: Set<string> },
): boolean {
  const rm = activeRoadmap(state, roadmaps);
  if (!rm) return false;
  const rmProjects = knownIds?.projects
    ? rm.projects.filter((id) => knownIds.projects!.has(id))
    : rm.projects;
  if (sortedJoin(state.selectedProjectIds) !== sortedJoin(rmProjects)) return true;
  const rmTeamChange =
    knownIds?.teamChanges &&
    rm.team_change != null &&
    !knownIds.teamChanges.has(rm.team_change)
      ? null
      : rm.team_change;
  if (state.selectedTeamChangeId !== rmTeamChange) return true;
  return false;
}

/**
 * Predicate for the Create Roadmap CTA: only available when no
 * roadmap is active AND the user has at least one project / team
 * change picked.
 */
export function canCreateRoadmap(state: RoadmapState): boolean {
  return (
    state.roadmapId === null &&
    (state.selectedProjectIds.length > 0 ||
      state.selectedTeamChangeId !== null)
  );
}

/**
 * Pick the team/ratings context to use for the current selection.
 * Reads `state.selectedTeamChangeId` directly — whether the value
 * came from a roadmap auto-load or a free-form pick is irrelevant to
 * the projection.
 */
export function projectionContext(input: {
  state: RoadmapState;
  engineers: Engineer[];
  roadmaps: Roadmap[];
  projectedRosters: Map<string, ProjectedRoster>;
  liveRatingIndex: RatingIndex;
}): RoadmapProjectionContext {
  const { state, engineers, projectedRosters, liveRatingIndex } = input;

  if (state.selectedTeamChangeId) {
    const pr = projectedRosters.get(state.selectedTeamChangeId);
    if (pr) return fromRoster(pr);
  }

  return {
    teamColumns: engineers.filter((e) => e.status === 'active'),
    ratings: liveRatingIndex,
    departedHumans: [],
    upskillDeltas: {},
    fromProjection: false,
    teamChangeId: null,
    teamChangeDisplay: null,
  };
}

function fromRoster(pr: ProjectedRoster): RoadmapProjectionContext {
  return {
    teamColumns: pr.talentColumns,
    ratings: pr.ratings,
    departedHumans: pr.departedHumans,
    upskillDeltas: pr.upskillDeltas,
    fromProjection: true,
    teamChangeId: pr.id,
    teamChangeDisplay: pr.display,
  };
}

// ===== Aggregate roadmap projects ======================================

/**
 * Union the currently-selected projects into a synthetic ProjectView
 * with `isAggregate: true`. Color = MAX level required, count = total
 * demand (multi-project pile-up surfaces concentration risk).
 * Per-project breakdown carried in `cell.contributions` for tooltip
 * attribution.
 *
 * `projectIds` is the caller's selection — typically
 * `state.selectedProjectIds` (multi-select sidebar) or a roadmap's
 * `rm.projects` (when the caller has already filtered to the
 * roadmap's contents).
 */
export function aggregateProjects(
  projectIds: string[],
  projectViews: Map<string, ProjectView>,
  display: string,
): ProjectView | null {
  const projs = projectIds
    .map((id) => projectViews.get(id))
    .filter((p): p is ProjectView => p !== undefined);
  if (projs.length === 0) return null;

  const compMap = new Map<string, MergedDerivedContext>();
  for (const p of projs) {
    for (const c of p.mergedDerivedContexts) {
      const existing = compMap.get(c.id);
      if (!existing) {
        compMap.set(c.id, {
          ...c,
          functions: [...c.functions],
          skills: c.skills.map((s) => ({ ...s })),
        });
      } else {
        for (const f of c.functions) {
          if (!existing.functions.includes(f)) existing.functions.push(f);
        }
        for (const s of c.skills) {
          if (!existing.skills.some((es) => es.id === s.id)) {
            existing.skills.push({ ...s });
          }
        }
        existing.is_overlay = existing.is_overlay || c.is_overlay;
      }
    }
  }
  const fnMap = new Map<string, MergedFunction>();
  for (const p of projs) {
    for (const f of p.mergedFunctions) {
      if (!fnMap.has(f.id)) fnMap.set(f.id, { ...f });
    }
  }
  const mergedDerivedContexts = [...compMap.values()];
  const mergedFunctions = [...fnMap.values()];

  const impactGrid: Record<string, Record<string, ProjectImpactCell>> = {};
  for (const c of mergedDerivedContexts) {
    const compCells: Record<string, ProjectImpactCell> = {};
    impactGrid[c.id] = compCells;
    for (const f of mergedFunctions) {
      compCells[f.id] = {
        maxLevel: 0,
        count: 0,
        skillCount: 0,
        demands: [],
        contributions: [],
      };
    }
  }
  // Profile counts add cleanly across projects: a `primary` role in
  // project A and a `primary` role in project B are two distinct
  // deliverables, so we sum per-project counts rather than dedupe
  // role IDs across the union.
  for (const p of projs) {
    for (const c of p.mergedDerivedContexts) {
      for (const f of p.mergedFunctions) {
        const perProj = p.impactGrid[c.id]?.[f.id];
        if (!perProj || perProj.skillCount === 0) continue;
        const cell = impactGrid[c.id]?.[f.id];
        if (!cell) continue;
        cell.count += perProj.count;
        cell.skillCount += perProj.skillCount;
        if (perProj.maxLevel > cell.maxLevel) cell.maxLevel = perProj.maxLevel;
        const contributions = cell.contributions ?? [];
        for (const d of perProj.demands) {
          contributions.push({ project: p.display, ...d });
        }
        cell.contributions = contributions;
      }
    }
  }

  const maxRequirements: Record<string, RatingValue> = {};
  for (const p of projs) {
    for (const roleReqs of Object.values(p.requirementsByRole)) {
      for (const [skillId, req] of Object.entries(roleReqs)) {
        const prev = maxRequirements[skillId];
        if (prev === undefined || req.level > prev) {
          maxRequirements[skillId] = req.level;
        }
      }
    }
  }

  return {
    id: '__aggregate__',
    display: `${display} (aggregate)`,
    status: 'aggregate',
    prompt: null,
    mergedDerivedContexts,
    mergedFunctions,
    impactGrid,
    roles: [],
    requirementsByRole: {},
    isAggregate: true,
    maxRequirements,
    projectGroups: projs.map((p) => ({
      id: p.id,
      display: p.display,
      roles: p.roles,
      requirementsByRole: p.requirementsByRole,
    })),
  };
}

// ===== Talent-centric resource projection ==============================

/**
 * Build a synthetic ProjectView for the Resource sub-view. Starts
 * with the full talent catalog (every visible skill in the snapshot)
 * and unions in any overlay-added derivedContexts / skills introduced
 * by toggled-on projects.
 *
 * Rationale: the Resource view answers "what does the team look like
 * under this roadmap?" — so every skill row stays visible, with
 * highlights for what changes (hires, departures, upskills, overlay
 * skills) rather than filtering the grid to demand-bearing skills
 * only.
 */
export function talentResourceProjection(input: {
  state: RoadmapState;
  derivedContexts: DerivedContext[];
  // Kept on the signature for future use (e.g. enriching the display
  // label with the active roadmap's name) — not read today.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  roadmaps: Roadmap[];
  projectViews: Map<string, ProjectView>;
  showLibraries?: boolean;
}): ProjectView | null {
  const { state, derivedContexts, projectViews } = input;
  const showLibraries = input.showLibraries ?? false;
  const toggledProjs: ProjectView[] = [];
  for (const pid of state.selectedProjectIds) {
    const pv = projectViews.get(pid);
    if (pv) toggledProjs.push(pv);
  }

  // Full talent catalog. Library skills are hidden by default
  // (matches Resilience / Talent / Resource); flip `showLibraries`
  // from the FilterViewBar toggle to surface them.
  const compMap = new Map<string, MergedDerivedContext>();
  for (const c of derivedContexts) {
    const visibleSkills = showLibraries
      ? c.skills
      : c.skills.filter(isDefaultVisibleSkill);
    if (visibleSkills.length === 0) continue;
    compMap.set(c.id, {
      ...c,
      skills: visibleSkills.map((s) => ({
        ...s,
        is_overlay: false,
      })),
      is_overlay: false,
    });
  }

  // Union overlay derivedContexts / skills from toggled-on projects.
  // The visibility filter applies here too — without it, overlay
  // library skills slip through even with `showLibraries=false`.
  for (const p of toggledProjs) {
    for (const comp of p.mergedDerivedContexts) {
      const overlaySkills = showLibraries
        ? comp.skills
        : comp.skills.filter(isDefaultVisibleSkill);
      const existing = compMap.get(comp.id);
      if (!existing) {
        if (overlaySkills.length === 0) continue;
        compMap.set(comp.id, {
          ...comp,
          skills: overlaySkills.map((s) => ({ ...s, is_overlay: true })),
          is_overlay: true,
        });
      } else {
        for (const s of overlaySkills) {
          if (existing.skills.some((es) => es.id === s.id)) continue;
          existing.skills.push({ ...s, is_overlay: true });
        }
      }
    }
  }

  const mergedDerivedContexts = [...compMap.values()];

  const projectGroups = toggledProjs.map((p) => ({
    id: p.id,
    display: p.display,
    roles: p.roles,
    requirementsByRole: p.requirementsByRole,
  }));

  const maxRequirements: Record<string, RatingValue> = {};
  for (const p of toggledProjs) {
    for (const roleReqs of Object.values(p.requirementsByRole)) {
      for (const [skillId, req] of Object.entries(roleReqs)) {
        const prev = maxRequirements[skillId];
        if (prev === undefined || req.level > prev) {
          maxRequirements[skillId] = req.level;
        }
      }
    }
  }

  // Title surface: single project → its name; multiple → just "Talent".
  // (The active roadmap's display used to appear here, but we no
  // longer have direct access to it from state alone — the caller
  // can wrap the returned view to add roadmap framing if needed.)
  const display =
    toggledProjs.length === 1
      ? `${toggledProjs[0]!.display} (talent)`
      : 'Talent';

  return {
    id: '__talent__',
    display,
    status: 'talent',
    prompt: null,
    mergedDerivedContexts,
    mergedFunctions: [],
    impactGrid: {},
    roles: [],
    requirementsByRole: {},
    isAggregate: true,
    maxRequirements,
    projectGroups,
  };
}

// ===== Executability & supply ==========================================

function lookupSkillDisplay(skillId: string, project: ProjectView): string {
  for (const comp of project.mergedDerivedContexts) {
    const skill = comp.skills.find((s) => s.id === skillId);
    if (skill) return skill.display;
  }
  return skillId;
}

function classifyExecutability(count: number): ExecutabilityClass {
  if (count === 0) return 'gap';
  if (count === 1) return 'fragile';
  return 'covered';
}

function engineerKind(eng: Engineer | ProjectedTalentColumn): 'human' | 'agent' {
  if ('kind' in eng && eng.kind === 'agent') return 'agent';
  return 'human';
}

/**
 * Per required skill, count engineers who clear the strictest bar
 * using the active projection context. Agents excluded (caps convey
 * fragility separately). Classification: 0 → gap, 1 → fragile, ≥2 →
 * covered.
 */
export function computeExecutability(
  project: ProjectView,
  ctx: RoadmapProjectionContext,
): ExecutabilityRow[] {
  const requirements = new Map<string, RatingValue>();
  if (project.isAggregate && project.maxRequirements) {
    for (const [skillId, lvl] of Object.entries(project.maxRequirements)) {
      requirements.set(skillId, lvl);
    }
  } else {
    for (const roleReqs of Object.values(project.requirementsByRole)) {
      for (const [skillId, req] of Object.entries(roleReqs)) {
        const prev = requirements.get(skillId);
        if (prev === undefined || req.level > prev) {
          requirements.set(skillId, req.level);
        }
      }
    }
  }
  const result: ExecutabilityRow[] = [];
  for (const [skillId, level] of requirements) {
    let count = 0;
    const qualified: string[] = [];
    for (const eng of ctx.teamColumns) {
      if (engineerKind(eng) === 'agent') continue;
      const rating = ctx.ratings.get(eng.id)?.get(skillId);
      if (rating !== undefined && rating >= level) {
        count++;
        qualified.push(eng.display_name);
      }
    }
    result.push({
      skillId,
      skillDisplay: lookupSkillDisplay(skillId, project),
      level,
      count,
      cls: classifyExecutability(count),
      qualified,
    });
  }
  return result;
}

// ===== Reducer =========================================================

export type RoadmapAction =
  | { type: 'select-roadmap'; roadmapId: string | null; roadmap: Roadmap | null }
  | { type: 'toggle-project'; projectId: string }
  | { type: 'select-team-change'; teamChangeId: string | null }
  | { type: 'set-view'; view: RoadmapViewMode }
  // Reconcile the live selection against a freshly-fetched view. The
  // reducer is seeded once via `initialRoadmapState` (lazy init) and
  // never re-runs, so after a plan create/update/delete the refetched
  // `view` must be folded back in: prune selected ids that no longer
  // exist (e.g. a deleted plan), and optionally pin a just-saved plan
  // so it's visible in-session without a manual refresh. `ensureProject`
  // / `ensureTeamChange` carry the id of a newly created/updated plan to
  // select; absent = pure prune.
  | {
      type: 'sync-view';
      projectIds: Set<string>;
      teamChangeIds: Set<string>;
      ensureProject?: string;
      ensureTeamChange?: string;
    }
  | { type: 'open-forecast' }
  | { type: 'close-forecast' };

/**
 * Compute the starting `RoadmapState`. Auto-selects the first
 * roadmap when any exist; otherwise lands on the bare platform map.
 *
 * The `select-roadmap` reducer case mirrors this: loading
 * `rm.projects` + `rm.team_change` into the selection fields so the
 * grid renders the roadmap's contents on arrival.
 */
export function initialRoadmapState(input: {
  roadmaps: Roadmap[];
  projects: Project[];
}): RoadmapState {
  const firstRoadmap = input.roadmaps[0] ?? null;
  const firstProject = input.projects[0] ?? null;
  // Orgs with no roadmaps + no projects land on Resource (the platform
  // map: engineers × skills). Roadmaps / projects / team-changes are
  // overlays on top of that base — there's nothing to overlay yet for
  // a fresh org. Orgs with anything to land on keep Impact as the
  // default so the first roadmap's impact view loads on arrival.
  const hasLandingTarget = firstRoadmap !== null || firstProject !== null;
  return {
    roadmapId: firstRoadmap?.id ?? null,
    selectedProjectIds: firstRoadmap
      ? [...firstRoadmap.projects]
      : firstProject
        ? [firstProject.id]
        : [],
    selectedTeamChangeId: firstRoadmap?.team_change ?? null,
    view: hasLandingTarget ? 'impact' : 'resource',
    forecastOpen: false,
  };
}

export function roadmapReducer(
  state: RoadmapState,
  action: RoadmapAction,
): RoadmapState {
  switch (action.type) {
    case 'select-roadmap': {
      // Loading a roadmap snapshots its projects + team_change into
      // the live selection; the user can then toggle items to see
      // the diff (which surfaces the Update Roadmap CTA). Selecting
      // null (the "No roadmap" sentinel in the dropdown) clears
      // the selection entirely.
      return {
        ...state,
        roadmapId: action.roadmapId,
        selectedProjectIds: action.roadmap ? [...action.roadmap.projects] : [],
        selectedTeamChangeId: action.roadmap?.team_change ?? null,
      };
    }
    case 'toggle-project': {
      const present = state.selectedProjectIds.includes(action.projectId);
      const next = present
        ? state.selectedProjectIds.filter((id) => id !== action.projectId)
        : [...state.selectedProjectIds, action.projectId];
      return { ...state, selectedProjectIds: next };
    }
    case 'select-team-change':
      return { ...state, selectedTeamChangeId: action.teamChangeId };
    case 'sync-view': {
      // Prune project ids that no longer exist, then union in a
      // just-saved one. `ensureProject` is appended (not toggled) so a
      // freshly created plan lands selected and the grid renders it; an
      // already-selected id is a no-op. Order is irrelevant (treated as
      // a set downstream).
      const pruned = state.selectedProjectIds.filter((id) =>
        action.projectIds.has(id),
      );
      const nextProjects =
        action.ensureProject && !pruned.includes(action.ensureProject)
          ? [...pruned, action.ensureProject]
          : pruned;
      // Team-change is single-select: keep the current one if it still
      // exists, override with a just-saved one, else clear a dangling
      // ref.
      const nextTeamChange = action.ensureTeamChange
        ? action.ensureTeamChange
        : state.selectedTeamChangeId &&
            action.teamChangeIds.has(state.selectedTeamChangeId)
          ? state.selectedTeamChangeId
          : null;
      // Idempotent: bail with the same reference when nothing moved so
      // the effect firing on every background refetch can't churn
      // renders or reset selections on a no-op.
      const projectsSame =
        nextProjects.length === state.selectedProjectIds.length &&
        nextProjects.every((id, i) => id === state.selectedProjectIds[i]);
      if (projectsSame && nextTeamChange === state.selectedTeamChangeId) {
        return state;
      }
      return {
        ...state,
        selectedProjectIds: nextProjects,
        selectedTeamChangeId: nextTeamChange,
      };
    }
    case 'set-view':
      return { ...state, view: action.view };
    case 'open-forecast':
      return { ...state, forecastOpen: true };
    case 'close-forecast':
      return { ...state, forecastOpen: false };
  }
}

/**
 * Diff-view supply count: engineers who clear the cell's max level
 * on at least one of the cell's demanded skills. Same agent-exclusion
 * rule as `computeExecutability`.
 */
export function countQualifiedEngineers(
  cell: ProjectImpactCell | null | undefined,
  ctx: RoadmapProjectionContext,
): { count: number; qualified: string[] } {
  if (!cell || cell.skillCount === 0 || cell.maxLevel === 0) {
    return { count: 0, qualified: [] };
  }
  const items = cell.contributions ?? cell.demands;
  const cellSkills = new Set<string>();
  for (const it of items) cellSkills.add(it.skill);
  if (cellSkills.size === 0) return { count: 0, qualified: [] };

  const level = cell.maxLevel;
  let count = 0;
  const qualified: string[] = [];
  for (const eng of ctx.teamColumns) {
    if (engineerKind(eng) === 'agent') continue;
    const ratings = ctx.ratings.get(eng.id);
    if (!ratings) continue;
    let best = 0;
    for (const skillId of cellSkills) {
      const r = ratings.get(skillId) ?? 0;
      if (r > best) best = r;
    }
    if (best >= level) {
      count++;
      qualified.push(eng.display_name);
    }
  }
  return { count, qualified };
}

/** Re-export for sub-components that need the institutional check. */
export { isInstitutional };
